import { ProjectData, RabEntry, ReportMetadata } from '../../types';
import type { UnterlageAblegen } from '../../services/unterlagenAblage';

/**
 * Reine Regeln für die Ablage der Unterlage für spätere Arbeiten in der
 * Projektablage („Dokumente", Stufe 1A des Planungsphasen-Plans).
 *
 * Hier steht nur, WAS abgelegt wird und WIE es heißt — ohne Supabase, ohne
 * jsPDF, damit es ohne Browser prüfbar bleibt. Das Schreiben selbst läuft
 * über `services/unterlagenAblage.ts`, der eine Ablageweg aller Generatoren.
 *
 * ═══ WANN ABGELEGT WIRD (Entscheid E17) ═══════════════════════════════════
 * Automatisch bei Freigabe und Versand, Entwürfe nur per Knopf — sonst füllt
 * sich die Ablage mit Zwischenständen. Die Unterlage kennt keinen eigenen
 * Freigabeschritt und keinen Versand; ihr Fertigstellen ist der Export:
 *
 *   • Export einer FINALEN Unterlage (`isDraft` aus)  → Fassung ablegen.
 *   • Export eines Entwurfs MIT Leistungsbuchung       → ablegen, deutlich
 *     als Entwurf beschriftet: was abgerechnet wurde, ist herausgegangen und
 *     muss nachweisbar bleiben, auch wenn es als Entwurf gekennzeichnet war.
 *   • Export eines Entwurfs ohne Buchung               → NICHT automatisch;
 *     dafür gibt es den Knopf „Entwurf ablegen".
 *   • Die Vorschau im Modul legt nie ab — sie ist nur die Ansicht des PDFs.
 *
 * ═══ DIE KENNUNG EINES STANDES (`fassungRef`) ═════════════════════════════
 * Die Ablage legt je Fassung eine eigene Zeile an und überschreibt nichts.
 * Damit derselbe Stand nach einem zweiten Export nicht zweimal liegt, trägt
 * jede Fassung eine Kennung, die sich NUR mit dem Inhalt des PDFs ändert: ein
 * Hash über Projektdaten, Maßnahmenkatalog UND die Ausgabeoptionen, die das
 * Blatt verändern (Regelwerksspalte, Querformat, Titelbildgröße). `version`
 * allein reicht nicht — sie wird von Hand gepflegt und bleibt stehen, während
 * der Katalog wächst; und derselbe Katalog im Querformat ist ein anderes
 * Blatt, das sonst als „schon abgelegt" verworfen würde.
 *
 * ═══ FREIGABE IST AUSGABE (Stufe 6.1/6.3) ═════════════════════════════════
 * Eine finale Fassung geht mit `ausgabe` an den Kern: der legt neben der Zeile
 * den Journal-Eintrag mit dem Steckbrief-Auszug an — der Anker, gegen den der
 * Änderungsdienst später vergleicht. Ein Entwurf (auch ein abgerechneter) ist
 * keine Ausgabe und bekommt keinen Anker: er löst keine Rückfrage aus.
 */

/** Weshalb abgelegt wird; bestimmt Beschriftung und Beschreibung. */
export type UfsaAblageAnlass = 'freigabe' | 'entwurf';

/**
 * Entscheidet, ob ein Export automatisch in die Ablage geht — und als was.
 * `null` heißt: nicht automatisch (Entwurf ohne Buchung, siehe Dateikopf).
 */
export const ufsaAblageAnlassFuerExport = (
    a: { isDraft: boolean; gebucht: boolean },
): UfsaAblageAnlass | null => {
    if (!a.isDraft) return 'freigabe';
    if (a.gebucht) return 'entwurf';
    return null;
};

/** Der Anlass für das Ablegen von Hand: Entwürfe als Entwurf, alles andere als Fassung. */
export const ufsaAblageAnlassVonHand = (isDraft: boolean): UfsaAblageAnlass =>
    isDraft ? 'entwurf' : 'freigabe';

// ─── Stabile Kennung ─────────────────────────────────────────────────────────

/**
 * JSON mit sortierten Schlüsseln, rekursiv.
 *
 * `JSON.stringify` hängt an der Einfügereihenfolge der Schlüssel; ein aus
 * einer Version wiederhergestellter Stand hat dieselben Werte, aber nicht
 * zwingend dieselbe Reihenfolge — und bekäme sonst eine andere Kennung.
 * `undefined` fällt wie bei JSON.stringify weg, damit ein fehlendes Feld und
 * ein ausdrücklich nicht gesetztes gleich zählen.
 */
const stabilesJson = (wert: unknown): string => {
    if (wert === null || typeof wert !== 'object') return JSON.stringify(wert) ?? 'null';
    if (Array.isArray(wert)) return `[${wert.map(stabilesJson).join(',')}]`;
    const obj = wert as Record<string, unknown>;
    const teile = Object.keys(obj)
        .filter(k => obj[k] !== undefined)
        .sort()
        .map(k => `${JSON.stringify(k)}:${stabilesJson(obj[k])}`);
    return `{${teile.join(',')}}`;
};

/**
 * FNV-1a, 32 Bit — dasselbe Verfahren wie `geoCache.addressHash`. Kein
 * Sicherheitsmerkmal, nur ein Fingerabdruck zum Wiedererkennen; zwei Läufe mit
 * verschiedenem Startwert machen eine zufällige Kollision unwahrscheinlich.
 */
const fnv1a32 = (text: string, start: number): number => {
    let h = start >>> 0;
    for (let i = 0; i < text.length; i++) {
        h = (h ^ text.charCodeAt(i)) >>> 0;
        // h * 16777619, ausmultipliziert — bleibt in 32 Bit.
        h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h;
};

/**
 * Die Optionen des Generators, die das PDF sichtbar verändern — und damit zum
 * Stand gehören. `showTOC` wertet der Generator nicht aus, `output` ändert
 * nur, ob heruntergeladen wird; beide bleiben bewusst draußen.
 */
export interface UfsaAusgabeOptionen {
    showRegulations: boolean;
    orientation: 'portrait' | 'landscape';
    coverImageSize: 'small' | 'medium' | 'large';
}

/** Dieselben Vorgaben wie im Generator, damit „nicht gesetzt" und Vorgabe gleich zählen. */
const normalisiereOptionen = (o: Partial<UfsaAusgabeOptionen> | undefined): UfsaAusgabeOptionen => ({
    showRegulations: o?.showRegulations !== false,
    orientation: o?.orientation === 'landscape' ? 'landscape' : 'portrait',
    coverImageSize: o?.coverImageSize || 'medium',
});

/**
 * Fingerabdruck des Blattes: Projektdaten UND Katalog, einschließlich
 * Anhangsbilder, Logo und Entwurfskennzeichen, plus die wirksamen
 * Ausgabeoptionen — alles, was im PDF steht. Ein Entwurf und seine finale
 * Fassung mit gleichem Text sind zwei Stände, Hoch- und Querformat ebenso.
 */
export const ufsaInhaltsHash = (
    projectData: ProjectData,
    entries: RabEntry[],
    optionen?: Partial<UfsaAusgabeOptionen>,
): string => {
    const text = stabilesJson({ projectData, entries, optionen: normalisiereOptionen(optionen) });
    return fnv1a32(text, 0x811c9dc5).toString(16).padStart(8, '0')
        + fnv1a32(text, 0x9dc5811c).toString(16).padStart(8, '0');
};

/** Die Kennung des Standes, wie sie in `fassung_ref` der Ablage steht. */
export const ufsaFassungRef = (
    projectData: ProjectData,
    entries: RabEntry[],
    optionen?: Partial<UfsaAusgabeOptionen>,
): string => `ufsa:${ufsaInhaltsHash(projectData, entries, optionen)}`;

// ─── Beschriftung ────────────────────────────────────────────────────────────

/** ISO-Datum (YYYY-MM-DD, auch mit Uhrzeit dahinter) deutsch; Unlesbares bleibt stehen. */
export const ufsaDatumDeutsch = (iso: string | null | undefined): string => {
    const s = (iso || '').slice(0, 10);
    const t = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    return t ? `${t[3]}.${t[2]}.${t[1]}` : (iso || '');
};

/** Heute als ISO-Datum ohne Zeitzonenversatz (toISOString kippt abends auf den Vortag). */
export const ufsaHeuteIso = (jetzt: Date = new Date()): string => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${jetzt.getFullYear()}-${p(jetzt.getMonth() + 1)}-${p(jetzt.getDate())}`;
};

const fassungsText = (version: string | undefined | null): string => {
    const v = (version || '').trim();
    return v ? `Fassung ${v}` : 'Fassung ohne Nummer';
};

/**
 * Der Stand, wie er in der Ablage angezeigt wird („Rev. C" bei fremden
 * Plänen): Fassungsnummer und Tag des Ablegens, bei Entwürfen vorangestellt.
 */
export const ufsaAblageStand = (
    projectData: Pick<ProjectData, 'version'>,
    anlass: UfsaAblageAnlass,
    abgelegtAmIso: string,
): string => {
    const kern = `${fassungsText(projectData.version)} · ${ufsaDatumDeutsch(abgelegtAmIso)}`;
    return anlass === 'entwurf' ? `Entwurf · ${kern}` : kern;
};

/**
 * Der Titel der abgelegten Zeile. Ein Entwurf trägt das Wort vorn und in
 * Großbuchstaben — in der Liste der Ablage soll niemand einen Zwischenstand
 * für die geltende Fassung halten.
 */
export const ufsaAblageTitel = (
    projectData: Pick<ProjectData, 'version'>,
    anlass: UfsaAblageAnlass,
    abgelegtAmIso: string,
): string => {
    const kern = `Unterlage für spätere Arbeiten – ${fassungsText(projectData.version)} – ${ufsaDatumDeutsch(abgelegtAmIso)}`;
    return anlass === 'entwurf' ? `ENTWURF ${kern}` : kern;
};

/** Beschreibung der Zeile: woher sie kommt und ob sie abgerechnet wurde. */
export const ufsaAblageBeschreibung = (
    anlass: UfsaAblageAnlass,
    a: { gebucht: boolean; vonHand: boolean; massnahmen: number },
): string => {
    const woher = a.vonHand
        ? 'Von Hand aus dem Modul abgelegt'
        : a.gebucht
            ? 'Beim Export mit Leistungsbuchung abgelegt'
            : 'Beim Export abgelegt';
    const was = anlass === 'entwurf'
        ? 'Entwurf — noch nicht die geltende Fassung.'
        : 'Finale Fassung.';
    return `${woher}. ${was} ${a.massnahmen} Maßnahmen im Katalog.`;
};

/**
 * Dateiname des Unterlagen-PDFs — DERSELBE für Download und Ablage.
 *
 * Er stand bisher nur im Generator; herunterladen und ablegen müssen gleich
 * heißen, sonst liegen im Posteingang und in der Ablage zwei Dateien, denen
 * niemand ansieht, dass sie dasselbe Blatt sind.
 */
export const unterlagePdfDateiname = (projectName: string | null | undefined): string =>
    `Unterlage_${(projectName || 'Projekt').replace(/[^a-z0-9äöüß]/gi, '_')}.pdf`;

// ─── Warnung trotz Erfolg ────────────────────────────────────────────────────

/**
 * Der Satz, der bei `schon_abgelegt` zusätzlich angezeigt werden muss — oder
 * `null`, wenn es nichts zu melden gibt.
 *
 * Der Kern meldet in diesem Fall nur dann einen `fehler`, wenn die doppelt
 * hochgeladene Datei nicht wieder entfernt werden konnte (`verwaisteDatei`).
 * Die Fassung liegt dann zwar, aber im Bucket blieb eine Datei ohne Zeile
 * zurück — das darf nicht wie uneingeschränkter Erfolg aussehen. Kein Retry:
 * ein erneutes Ablegen fände dieselbe Fassung und änderte an der Waise nichts.
 */
export const ufsaAblageWarnung = (ergebnis: {
    status: 'abgelegt' | 'schon_abgelegt' | 'fehler';
    fehler: string | null;
    verwaisteDatei?: string | null;
}): string | null => {
    if (ergebnis.status !== 'schon_abgelegt') return null;
    const satz = (ergebnis.fehler || '').trim();
    const pfad = (ergebnis.verwaisteDatei || '').trim();
    if (!satz && !pfad) return null;
    const kern = satz || 'Eine doppelt hochgeladene Datei konnte nicht wieder entfernt werden.';
    return pfad ? `${kern} Verwaiste Datei: ${pfad}` : kern;
};

/**
 * Die Hinweise des Kerns zu einem GELUNGENEN Ergebnis — etwa „Journal-Eintrag
 * nicht angelegt" (Stufe 6.3). Keine Fehler: die Fassung liegt, Export und
 * Buchung gelten. Dezent anzeigen, nie verschlucken. Bei `fehler` steht alles
 * im Fehlersatz; dann `[]`.
 */
export const ufsaAblageHinweise = (ergebnis: {
    status: 'abgelegt' | 'schon_abgelegt' | 'fehler';
    hinweise?: string[];
}): string[] => {
    if (ergebnis.status === 'fehler') return [];
    return (ergebnis.hinweise || []).map(h => (h || '').trim()).filter(Boolean);
};

// ─── Der Ablageauftrag ───────────────────────────────────────────────────────

export interface UfsaAuftragArgs {
    projectId: string;
    /** Die Bytes GENAU des PDFs, das exportiert wurde — nicht ein später neu gesetztes. */
    datei: Blob;
    projectData: ProjectData;
    entries: RabEntry[];
    optionen: Partial<UfsaAusgabeOptionen>;
    anlass: UfsaAblageAnlass;
    gebucht: boolean;
    vonHand: boolean;
    /** Tag des Ablegens, ISO — von außen, damit die Funktion rein bleibt. */
    abgelegtAmIso: string;
    /** Projektdaten für den Steckbrief-Auszug des Journals — nur bei `freigabe` ausgewertet. */
    metadata?: Partial<ReportMetadata> | null;
}

/**
 * Baut den vollständigen Auftrag für `legeUnterlageAb` — EINMAL, beim Export.
 *
 * Der Auftrag trägt die Bytes und alle Kennzeichen des exportierten Blattes
 * zusammen. Ein späteres „Erneut ablegen" schickt genau diesen Auftrag noch
 * einmal, statt den dann aktuellen Editorstand neu zu setzen: der Nachweis in
 * der Ablage muss das Blatt sein, das der Anwender wirklich in der Hand hat
 * (und abgerechnet hat), nicht das, woran er inzwischen weitergearbeitet hat.
 */
export const ufsaAblageAuftrag = (a: UfsaAuftragArgs): UnterlageAblegen => {
    const auftrag: UnterlageAblegen = {
        projectId: a.projectId,
        modul: 'unterlage',
        datei: a.datei,
        dateiname: unterlagePdfDateiname(a.projectData.projectName),
        titel: ufsaAblageTitel(a.projectData, a.anlass, a.abgelegtAmIso),
        stand: ufsaAblageStand(a.projectData, a.anlass, a.abgelegtAmIso),
        fassungRef: ufsaFassungRef(a.projectData, a.entries, a.optionen),
        anlass: a.anlass,
        beschreibung: ufsaAblageBeschreibung(a.anlass, {
            gebucht: a.gebucht, vonHand: a.vonHand, massnahmen: a.entries.length,
        }),
    };
    // Nur die finale Fassung ist eine Ausgabe (siehe Dateikopf).
    if (a.anlass === 'freigabe') {
        auftrag.ausgabe = { unterlage: 'unterlage', metadata: a.metadata ?? null };
    }
    return auftrag;
};
